import { useContext, useState } from "react";
import { Navigate, useLocation, useMatch } from "react-router-dom";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { buildClasses } from "../utils/buildClasses";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { MoneyFlowReport } from "./MoneyFlowReport";
import { TrendsReport } from "./TrendsReport";
import { CategoryBreakdownReport } from "./CategoryBreakdownReport";
import { TopMerchantsReport } from "./TopMerchantsReport";
import { CashFlowNav } from "./CashFlowNav";
import type {
  MoneyFlowReportState,
  TrendsReportState,
  CategoryBreakdownReportState,
  TopMerchantsReportState,
  CashFlowState,
} from "./cashFlowState";
import {
  CASH_FLOW_VIEWS,
  cashFlowViewPath,
  CashFlowView,
} from "./cashFlowViews";

function buildInitialCashFlowState(): CashFlowState {
  return {
    moneyFlow: {
      includeUnreviewed: true,
      dateRange: { preset: "year-to-date", startMonth: null, endMonth: null },
    },
    trends: {
      // The last 12 months, so a trend has enough months to show even early
      // in the year.
      dateRange: { preset: "last-12-months", startMonth: null, endMonth: null },
      chartType: "bars",
    },
    categoryBreakdown: {
      dateRange: { preset: "year-to-date", startMonth: null, endMonth: null },
      type: "expenses",
      level: "categories",
      chartType: "bars",
      barsType: "all",
    },
    topMerchants: {
      dateRange: { preset: "year-to-date", startMonth: null, endMonth: null },
      type: "expenses",
      sort: "total",
    },
  };
}

export function CashFlowScreen(): JSX.Element {
  const location = useLocation();
  const viewMatch = useMatch("/cash-flow/:view");
  const legacyBudgetTrackingMatch = useMatch("/reports/budget-tracking");
  const activeView =
    CASH_FLOW_VIEWS.find((view) => view.slug === viewMatch?.params.view) ??
    CASH_FLOW_VIEWS[0];
  const loadingInitialData = useContext(LoadingInitialDataContext);
  const [cashFlowState, setCashFlowState] = useState<CashFlowState>(
    buildInitialCashFlowState,
  );

  function setMoneyFlowState(
    update: (prev: MoneyFlowReportState) => MoneyFlowReportState,
  ) {
    setCashFlowState((prev) => ({
      ...prev,
      moneyFlow: update(prev.moneyFlow),
    }));
  }

  function setTrendsState(
    update: (prev: TrendsReportState) => TrendsReportState,
  ) {
    setCashFlowState((prev) => ({
      ...prev,
      trends: update(prev.trends),
    }));
  }

  function setCategoryBreakdownState(
    update: (
      prev: CategoryBreakdownReportState,
    ) => CategoryBreakdownReportState,
  ) {
    setCashFlowState((prev) => ({
      ...prev,
      categoryBreakdown: update(prev.categoryBreakdown),
    }));
  }

  function setTopMerchantsState(
    update: (prev: TopMerchantsReportState) => TopMerchantsReportState,
  ) {
    setCashFlowState((prev) => ({
      ...prev,
      topMerchants: update(prev.topMerchants),
    }));
  }

  const isLoading =
    loadingInitialData.transactions || loadingInitialData.categories;

  // Budget tracking moved to the Budgets tab.
  if (legacyBudgetTrackingMatch) {
    return <Navigate to="/budgets" replace />;
  }

  // Sends /cash-flow, unknown views, and old /reports links to a real view.
  // Replacing also prevents invalid/legacy URLs from adding a history entry.
  const activeViewPath = cashFlowViewPath(activeView.slug);
  if (location.pathname !== activeViewPath) {
    return (
      <Navigate
        to={{
          pathname: activeViewPath,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  // Keep view-specific state wiring here. The registry-derived type ensures
  // every view has a renderer, while only the selected element is mounted.
  const viewContent: Record<CashFlowView, JSX.Element> = {
    "sankey-flow": (
      <MoneyFlowReport
        state={cashFlowState.moneyFlow}
        setState={setMoneyFlowState}
      />
    ),
    "historical-trends": (
      <TrendsReport state={cashFlowState.trends} setState={setTrendsState} />
    ),
    "category-breakdown": (
      <CategoryBreakdownReport
        state={cashFlowState.categoryBreakdown}
        setState={setCategoryBreakdownState}
      />
    ),
    "top-merchants": (
      <TopMerchantsReport
        state={cashFlowState.topMerchants}
        setState={setTopMerchantsState}
      />
    ),
  };

  return (
    <div
      className={buildClasses(
        "flex",
        "flex-grow",
        "min-w-1",
        "h-full",
        "gap-2",
      )}
    >
      <CashFlowNav activeView={activeView.slug} />
      <RyeScreenWrapper>
        {isLoading ? (
          <RyeMainContentPanel title={activeView.label}>
            <div role="status" aria-label="Loading cash flow">
              <RyeSkeleton
                className={buildClasses(
                  "block",
                  "h-[60vh]",
                  "w-full",
                  "rounded-lg",
                )}
              />
            </div>
          </RyeMainContentPanel>
        ) : (
          viewContent[activeView.slug]
        )}
      </RyeScreenWrapper>
    </div>
  );
}
